import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface TestModeContextType {
  testMode: boolean;
  toggleTestMode: () => void;
  setTestMode: (enabled: boolean) => void;
  getDbPath: (basePath: string) => string;
}

const TestModeContext = createContext<TestModeContextType | null>(null);

const TEST_MODE_STORAGE_KEY = 'app_test_mode';

/**
 * Provides test/production mode toggle and persistence to the component tree.
 * The mode is persisted in localStorage so it survives page refreshes.
 */
export const TestModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [testMode, setTestModeState] = useState<boolean>(() => {
    // Initialize from localStorage if available
    const stored = localStorage.getItem(TEST_MODE_STORAGE_KEY);
    return stored === 'true';
  });

  // Persist to localStorage when changed
  useEffect(() => {
    localStorage.setItem(TEST_MODE_STORAGE_KEY, String(testMode));
  }, [testMode]);

  const toggleTestMode = useCallback(() => {
    setTestModeState((prev) => !prev);
  }, []);

  const setTestMode = useCallback((enabled: boolean) => {
    setTestModeState(enabled);
  }, []);

  // Helper to get the correct database path based on test mode
  const getDbPath = useCallback((basePath: string): string => {
    if (testMode) {
      return `testMode/${basePath}`;
    }
    return basePath;
  }, [testMode]);

  const value = {
    testMode,
    toggleTestMode,
    setTestMode,
    getDbPath,
  };

  return (
    <TestModeContext.Provider value={value}>
      {children}
    </TestModeContext.Provider>
  );
};

/**
 * Consume the TestModeContext. Must be used within a TestModeProvider.
 *
 * @returns Test mode state, toggle function, and DB path helper.
 */
export const useTestMode = () => {
  const context = useContext(TestModeContext);
  if (!context) {
    throw new Error('useTestMode must be used within a TestModeProvider');
  }
  return context;
};

export default TestModeContext;
